import {
  AndJoint,
  Criteria,
  EqFilter,
  ExistsFilter,
  Expression,
  Filter,
  GteFilter,
  GtFilter,
  ICriteriaTranslator,
  IdsFilter,
  InFilter,
  Joint,
  KeywordFilter,
  LikeFilter,
  LteFilter,
  LtFilter,
  NestedFilter,
  OrJoint,
  Pageable,
  PrefixFilter,
  QuickFilter,
  RangeFilter,
  Sort,
  TermsFilter,
} from 'slipstream-components';
import MatchLikeFilter from '../extendsFilters/MatchLikeFilter';
import RegexpLikeFilter from '../extendsFilters/RegexpLikeFilter';

export default class CSOTranslator implements ICriteriaTranslator {
  public translateToPost(c: Criteria) {
    let filterPart = null;
    const filter = c.filter;
    if (c.filter) {
      filterPart = {
        query:
          filter instanceof AndJoint || filter instanceof OrJoint
            ? this.translateJointToPost(filter)
            : this.translateSingleFilterToPost(filter),
      };
    } else {
      filterPart = {
        query: {
          match_all: {},
        },
      };
    }

    if (c.pageable) {
      Object.assign(filterPart, this.translatePageableToPost(c.pageable));
    }
    if (c.sort) {
      Object.assign(filterPart, this.translateSortToPost(c.sort));
    }

    filterPart.detail = true;
    return filterPart;
  }
  public translateSingleFilterToPost(filter) {
    if (filter instanceof InFilter) {
      if (filter.not) {
        return { bool: { must_not: this.translateExpressionToPost(filter as Expression) } };
      } else {
        return { bool: { must: this.translateExpressionToPost(filter as Expression) } };
      }
    } else {
      if (filter.not) {
        return { bool: { must_not: [this.translateExpressionToPost(filter as Expression)] } };
      } else {
        return { bool: { must: this.translateExpressionToPost(filter as Expression) } };
      }
    }
  }
  public translateJointToPost(joint: Joint) {
    if (joint instanceof AndJoint) {
      if (joint.filters) {
        const must: any[] = [];
        const mustnot: any[] = [];
        joint.filters.forEach((filter: Filter) => {
          if (filter instanceof Joint) {
            must.push(this.translateJointToPost(filter));
          } else {
            const expression = filter as Expression;
            if (expression.not && !(expression instanceof NestedFilter)) {
              mustnot.push(this.translateExpressionToPost(expression));
            } else if (filter instanceof QuickFilter) {
              // ignore quick filter
            } else {
              must.push(this.translateExpressionToPost(expression));
            }
          }
        });
        const postBody: any = {};
        if (must.length > 0) {
          postBody.must = must;
        }
        if (mustnot.length > 0) {
          postBody.must_not = mustnot;
        }
        return { bool: postBody };
      }
    } else {
      if (joint.filters) {
        const shouldFilters = [];
        joint.filters.forEach((filter: Filter) => {
          if (filter instanceof Joint) {
            shouldFilters.push(this.translateJointToPost(filter));
          } else {
            const expression = filter as Expression;
            shouldFilters.push(this.translateExpressionToPost(expression));
          }
        });
        return { bool: { should: shouldFilters } };
      }
    }
  }
  public translateExpressionToPost(exp: Expression) {
    if (exp instanceof EqFilter) {
      const term = {
        term: {},
      };
      const value = exp.value;
      if (typeof value === 'boolean' || typeof value === 'number') {
        term.term[exp.field] = exp.value;
      } else {
        term.term[exp.field + '._raw'] = exp.value;
      }
      return term;
    } else if (exp instanceof ExistsFilter) {
      const filterObj = { field: exp.field };
      if (exp.not) {
        return { bool: { must_not: { exists: filterObj } } };
      }
      return { exists: filterObj };
    } else if (exp instanceof NestedFilter) {
      const nestedFilter = exp as NestedFilter;
      if (
        nestedFilter.rootFilter instanceof AndJoint ||
        nestedFilter.rootFilter instanceof OrJoint
      ) {
        return {
          nested: {
            path: exp.path,
            query: this.translateJointToPost(nestedFilter.rootFilter),
          },
        };
      } else {
        return {
          nested: {
            path: exp.path,
            query: {
              bool: {
                must: [this.translateExpressionToPost(nestedFilter.rootFilter as Expression)],
              },
            },
          },
        };
      }
    } else if (exp instanceof RangeFilter) {
      const term = {
        range: {},
      };
      term.range[exp.field] = {
        to: exp.max,
        from: exp.min,
      };
      return term;
    } else if (exp instanceof TermsFilter || exp instanceof InFilter) {
      const term = {
        terms: {},
      };
      term.terms[exp.field + '._raw'] = exp.value;
      return term;
    } else if (exp instanceof PrefixFilter) {
      const query = {
        prefix: {},
      };
      query.prefix[exp.field] = exp.value;
      return query;
    } else if (exp instanceof KeywordFilter) {
      return {
        bool: {
          should: [
            {
              query_string: {
                query: `*${(exp.value as string).replace(/:/g, '\\:')}*`,
                analyze_wildcard: true,
                default_operator: 'AND',
              },
            },
          ],
        },
      };
    } else if (exp instanceof QuickFilter) {
      return null;
    } else if (exp instanceof LikeFilter) {
      const filterObj = {};
      filterObj[exp.field + '._raw'] = `.*${exp.value}+.*`;
      if (exp instanceof MatchLikeFilter) {
        if (exp.getMinMatchCount() !== 1) {
          filterObj[exp.field + '._raw'] = `(.*${exp.value}+.*){${exp.getMinMatchCount()},}`;
        }
      }
      if (exp instanceof RegexpLikeFilter) {
        filterObj[exp.field + '._raw'] = `${exp.value}`;
      }
      if (exp.not) {
        return { regexp: filterObj };
      } else {
        return {
          regexp: filterObj,
        };
      }
    } else if (exp instanceof LteFilter) {
      const query = {
        range: {},
      };
      query.range[exp.field] = {
        lte: exp.value,
      };
      return query;
    } else if (exp instanceof GteFilter) {
      const query = {
        range: {},
      };
      query.range[exp.field] = {
        gte: exp.value,
      };
      return query;
    } else if (exp instanceof GtFilter) {
      const query = {
        range: {},
      };
      query.range[exp.field] = {
        gt: exp.value,
      };
      return query;
    } else if (exp instanceof LtFilter) {
      const query = {
        range: {},
      };
      query.range[exp.field] = {
        lt: exp.value,
      };
      return query;
    }
    throw new Error("This filter doesn't support post");
  }
  public translateJointToGet(joint: Joint): string | string[] {
    let specialFilterStr = null;
    if (joint instanceof AndJoint) {
      const children = joint.getFilters();
      const keywordFilters = children.filter(f => f instanceof KeywordFilter);
      if (keywordFilters.length > 0) {
        specialFilterStr = 'q=';
        keywordFilters.forEach(k => {
          let keyword = (k as KeywordFilter).value;
          if (keyword) {
            keyword = (keyword + '').replace(/\ /g, '\\ ');
            keyword = (keyword + '').replace(/\//g, '\\/');
          }
          specialFilterStr += '*' + keyword;
        });
        specialFilterStr += '*';
      }

      const idsFilter = children.find(f => f instanceof IdsFilter);
      if (idsFilter) {
        if (specialFilterStr != null) {
          specialFilterStr += '&';
        } else {
          specialFilterStr = '';
        }
        specialFilterStr += 'obj_uuids=' + ((idsFilter as IdsFilter).value as string[]).join(',');
      }
    }

    let jointStr = null;
    if (joint instanceof AndJoint) {
      jointStr = 'and';
    } else {
      jointStr = 'or';
    }
    const filterStrs = joint.filters
      .filter(
        f => !(f instanceof KeywordFilter || f instanceof IdsFilter || f instanceof QuickFilter)
      )
      .map(f => {
        if (f instanceof Joint) {
          return this.translateJointToGet(f);
        } else {
          return this.translateExpressionToGet(f as Expression);
        }
      });
    let jointResult = null;
    if (filterStrs.length !== 0) {
      jointResult =
        filterStrs.length === 1 ? filterStrs[0] : '(' + filterStrs.join(' ' + jointStr + ' ') + ')';
    }

    return specialFilterStr ? [specialFilterStr, jointResult] : jointResult;
  }
  public translateExpressionToGet(exp: Expression) {
    if (exp instanceof EqFilter) {
      let str = encodeURIComponent(exp.field);
      str += (exp.not ? '!' : '') + '=';
      str +=
        typeof exp.value !== 'string' ? exp.value : "'" + encodeURIComponent('' + exp.value) + "'";
      return str;
    } else if (exp instanceof TermsFilter) {
      let str = encodeURIComponent(exp.field);
      str += (exp.not ? '!' : '') + '=';
      str +=
        typeof exp.value !== 'string' ? exp.value : "'" + encodeURIComponent('' + exp.value) + "'";
      return str;
    } else if (exp instanceof LikeFilter) {
      let str = encodeURIComponent(exp.field);
      str += exp.not ? ' not contain ' : ' contain ';
      str +=
        typeof exp.value !== 'string' ? exp.value : "'" + encodeURIComponent('' + exp.value) + "'";
      return str;
    } else if (exp instanceof GtFilter) {
      let str = encodeURIComponent(exp.field);
      str += '>';
      str +=
        typeof exp.value !== 'string' ? exp.value : "'" + encodeURIComponent('' + exp.value) + "'";
      return str;
    } else if (exp instanceof GteFilter) {
      let str = encodeURIComponent(exp.field);
      str += '>=';
      str +=
        typeof exp.value !== 'string' ? exp.value : "'" + encodeURIComponent('' + exp.value) + "'";
      return str;
    } else if (exp instanceof LteFilter) {
      let str = encodeURIComponent(exp.field);
      str += '<=';
      str +=
        typeof exp.value !== 'string' ? exp.value : "'" + encodeURIComponent('' + exp.value) + "'";
      return str;
    } else if (exp instanceof LtFilter) {
      let str = encodeURIComponent(exp.field);
      str += '<';
      str +=
        typeof exp.value !== 'string' ? exp.value : "'" + encodeURIComponent('' + exp.value) + "'";
      return str;
    } else if (exp instanceof InFilter) {
      const values = exp.value as string[];
      const strArr = ['('];
      const field = encodeURIComponent(exp.field);
      values.forEach((v, index) => {
        strArr.push(field);
        strArr.push(exp.not ? 'ne' : 'eq');
        strArr.push(`'${v}'`);
        if (index !== values.length - 1) {
          strArr.push('or');
        }
      });
      strArr.push(')');
      return strArr.join(' ');
    }
    throw new Error('Method not implemented.');
  }
  public translateToGet(c: Criteria): string {
    const urls = [];
    if (c.pageable) {
      urls.push(this.translatePageableToGet(c.pageable));
    }
    if (c.sort) {
      urls.push(this.translateSortToGet(c.sort));
    }
    if (c.filter) {
      if (c.filter instanceof Joint) {
        const jointResult = this.translateJointToGet(c.filter);
        // array means containing special filter.
        if (jointResult instanceof Array) {
          urls.push(jointResult[0]);
          if (jointResult[1]) {
            urls.push('filter=(' + jointResult[1] + ')');
          }
        } else {
          if (jointResult) {
            urls.push('filter=(' + jointResult + ')');
          }
        }
      } else {
        if (c.filter instanceof IdsFilter) {
          urls.push('obj_uuids=' + (c.filter.value as string[]).join(','));
        } else if (c.filter instanceof KeywordFilter) {
          let keyword = c.filter.value;
          if (keyword) {
            keyword = (keyword + '').replace(/\ /g, '\\ ');
            keyword = (keyword + '').replace(/\//g, '\\/');
          }
          const existingQIndex = urls.findIndex(u => u.startsWith('q=*'));
          if (existingQIndex !== -1) {
            urls[existingQIndex] = urls[existingQIndex] + keyword + '*';
          } else {
            urls.push('q=*' + keyword + '*');
          }
        } else {
          urls.push('filter=(' + this.translateExpressionToGet(c.filter as Expression) + ')');
        }
      }
    }
    return urls.join('&');
  }

  public translateSortToPost(sort: Sort) {
    const orders = sort.orders;
    return {
      sort: orders.map(od => {
        const payload: any = {};
        if (od.dataType !== 'string') {
          payload[od.field] = {
            order: od.direction === 'ascend' ? 'asc' : 'desc',
          };
        } else {
          payload[od.field + '._raw'] = {
            order: od.direction === 'ascend' ? 'asc' : 'desc',
          };
        }
        return payload;
      }),
    };
  }
  public translatePageableToPost(pagable: Pageable) {
    if (pagable.size > 0) {
      return {
        from: (pagable.page - 1) * pagable.size,
        size: pagable.size,
      };
    }
    return null;
  }
  public translateSortToGet(sort: Sort) {
    // only support one sort for now.
    const order = sort.orders[0];
    if (order) {
      return 'sorter=' + order.field + ':' + (order.direction === 'ascend' ? 'asc' : 'desc');
    } else {
      return '';
    }
  }
  public translatePageableToGet(pagable: Pageable) {
    const pStr = 'from=' + (pagable.page - 1) * pagable.size + '&size=' + pagable.size;
    return pStr;
  }
}
